import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { getMfaStatus, setupMfa, verifyMfaSetup, disableMfa } from '../api';

function MFASetup({ open, onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('status'); // 'status', 'setup', 'verify', 'backup', 'disable'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open]);

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getMfaStatus();
      setStatus(response.data);
      setStep('status');
    } catch (err) {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setActionLoading(true);
    setError('');
    try {
      const response = await setupMfa();
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start MFA setup');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    try {
      const response = await verifyMfaSetup(verifyCode);
      setBackupCodes(response.data.backupCodes);
      setStep('backup');
      setSuccess('MFA enabled successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    try {
      await disableMfa({ code: disableCode || undefined, password: disablePassword || undefined });
      setSuccess('MFA has been disabled.');
      setStatus({ enabled: false, backupCodesRemaining: 0 });
      setStep('status');
      setDisableCode('');
      setDisablePassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const text = backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setStep('status');
    setError('');
    setSuccess('');
    setVerifyCode('');
    setQrCode('');
    setSecret('');
    setBackupCodes([]);
    setDisableCode('');
    setDisablePassword('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Two-Factor Authentication (MFA)
        <Tooltip title="MFA adds a second layer of security to your account. After entering your password, you'll need to provide a code from an authenticator app (like Google Authenticator or Microsoft Authenticator) to complete sign-in.">
          <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Status View */}
            {step === 'status' && status && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <SecurityIcon sx={{ fontSize: 40, color: status.enabled ? 'success.main' : 'text.disabled' }} />
                  <Box>
                    <Typography variant="h6">
                      MFA is {status.enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                    {status.enabled && (
                      <Typography variant="body2" color="text.secondary">
                        {status.backupCodesRemaining} backup codes remaining
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={status.enabled ? 'Active' : 'Off'}
                    color={status.enabled ? 'success' : 'default'}
                    sx={{ ml: 'auto' }}
                  />
                </Box>

                {!status.enabled ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Protect your account with two-factor authentication. You'll need an authenticator
                      app like Google Authenticator, Microsoft Authenticator, or Authy.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>How to set up:</Typography>
                    <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 2, mb: 2 }}>
                      <li>Download an authenticator app on your phone</li>
                      <li>Click "Enable MFA" below</li>
                      <li>Scan the QR code with your authenticator app</li>
                      <li>Enter the 6-digit code to confirm</li>
                      <li>Save your backup codes in a safe place</li>
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleStartSetup}
                      disabled={actionLoading}
                      startIcon={actionLoading ? <CircularProgress size={16} /> : <SecurityIcon />}
                    >
                      Enable MFA
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => { setStep('disable'); setError(''); setSuccess(''); }}
                  >
                    Disable MFA
                  </Button>
                )}
              </Box>
            )}

            {/* Setup - QR Code */}
            {step === 'setup' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app, then enter the 6-digit code below to verify.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {qrCode && <img src={qrCode} alt="MFA QR Code" style={{ width: 200, height: 200 }} />}
                </Box>

                <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Can't scan? Enter this code manually:
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace" sx={{ mt: 0.5, letterSpacing: 2, wordBreak: 'break-all' }}>
                    {secret}
                  </Typography>
                </Paper>

                <form onSubmit={handleVerifySetup}>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={verifyCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerifyCode(val);
                    }}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    placeholder="000000"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => { setStep('status'); setError(''); }}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={actionLoading || verifyCode.length !== 6}
                    >
                      {actionLoading ? <CircularProgress size={20} /> : 'Verify & Enable'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}

            {/* Backup Codes */}
            {step === 'backup' && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Save these backup codes in a safe place. Each code can only be used once.
                  If you lose access to your authenticator app, you can use these to sign in.
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {backupCodes.map((code, i) => (
                      <Chip key={i} label={code} variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }} />
                    ))}
                  </Box>
                </Paper>

                <Button
                  variant="outlined"
                  startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  onClick={copyBackupCodes}
                  sx={{ mb: 2 }}
                  fullWidth
                >
                  {copied ? 'Copied!' : 'Copy All Codes'}
                </Button>

                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                  These codes will not be shown again.
                </Typography>
              </Box>
            )}

            {/* Disable MFA */}
            {step === 'disable' && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Disabling MFA will make your account less secure. To confirm, enter your current
                  authenticator code or your account password.
                </Alert>

                <form onSubmit={handleDisable}>
                  <TextField
                    fullWidth
                    label="Authenticator Code"
                    value={disableCode}
                    onChange={(e) => {
                      setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      if (e.target.value) setDisablePassword('');
                    }}
                    autoComplete="one-time-code"
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    placeholder="000000"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
                    or
                  </Typography>
                  <TextField
                    fullWidth
                    label="Account Password"
                    type="password"
                    value={disablePassword}
                    onChange={(e) => {
                      setDisablePassword(e.target.value);
                      if (e.target.value) setDisableCode('');
                    }}
                    autoComplete="current-password"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => { setStep('status'); setError(''); }}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      disabled={actionLoading || (!disableCode && !disablePassword)}
                    >
                      {actionLoading ? <CircularProgress size={20} /> : 'Disable MFA'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {step === 'backup' ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MFASetup;
